import DtoolsPdfEquipmentScheduleTable from "@/components/sales/dtools/DtoolsPdfEquipmentScheduleTable";
import DtoolsPdfQuotationPageHeader, {
  type DtoolsPdfQuotationHeaderInfo,
} from "@/components/sales/dtools/DtoolsPdfQuotationPageHeader";
import {
  BOM_PRINT_ACCENT,
  BOM_PRINT_BASE_FONT_SIZE_PX,
  BOM_PRINT_BASE_LINE_HEIGHT,
  BOM_PRINT_FONT,
  BOM_PRINT_TEXT,
} from "@/lib/bomPrintDesign";
import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import { buildDtoolsPdfScheduleLinesForSystem } from "@shared/dtoolsPdfQuotationSchedule";
import { buildDtoolsReviewSystemView, type DtoolsReviewBuildContext } from "@shared/dtoolsReviewNavigation";
import type { DtoolsReviewSystem } from "@shared/dtoolsStagingTransform";

type Props = {
  system: DtoolsReviewSystem;
  sectionLabel: string;
  header: DtoolsPdfQuotationHeaderInfo;
  reviewContext?: DtoolsReviewBuildContext;
  optionIndex?: number;
  showItemPricing: boolean;
  totalLabel?: string;
  /** When true, render only the repeating page chrome (header + section title). */
  chromeOnly?: boolean;
  /** When true, render only body content (overview, highlights, schedule, total). */
  bodyOnly?: boolean;
};

/** Pass through stored highlights exactly — split newlines only, never rewrite copy. */
function renderStoredHighlights(highlights: string[]): string[] {
  const out: string[] = [];
  for (const raw of highlights) {
    const trimmed = String(raw ?? "").trim();
    if (!trimmed) continue;
    if (trimmed.includes("\n")) {
      for (const line of trimmed.split("\n")) {
        const item = line.replace(/^[\s*•✓\-–—]+/, "").trim();
        if (item) out.push(item);
      }
      continue;
    }
    out.push(trimmed);
  }
  return out;
}

/** One System/Option quotation section: overview, highlights, schedule, total. */
export default function DtoolsPdfOptionQuotationSection({
  system,
  sectionLabel,
  header,
  reviewContext,
  optionIndex = 0,
  showItemPricing,
  totalLabel = "System Total",
  chromeOnly = false,
  bodyOnly = false,
}: Props) {
  const view = buildDtoolsReviewSystemView(system, reviewContext, optionIndex);
  const highlights = renderStoredHighlights(view.overview.highlights ?? []);
  const scheduleLines = buildDtoolsPdfScheduleLinesForSystem(system, sectionLabel);
  const overview = String(view.overview.description ?? "").trim();

  if (chromeOnly) {
    return <DtoolsPdfQuotationPageHeader info={header} sectionTitle={sectionLabel} />;
  }

  return (
    <div
      className="space-y-3"
      style={{
        fontFamily: BOM_PRINT_FONT,
        color: BOM_PRINT_TEXT,
        fontSize: BOM_PRINT_BASE_FONT_SIZE_PX,
        lineHeight: BOM_PRINT_BASE_LINE_HEIGHT,
      }}
    >
      {!bodyOnly ? <DtoolsPdfQuotationPageHeader info={header} sectionTitle={sectionLabel} /> : null}

      {overview ? (
        <section className="space-y-1 pdf-keep-together" data-testid="dtools-pdf-option-overview">
          <h3
            className="font-semibold uppercase"
            style={{ fontSize: 9, letterSpacing: "0.05em", color: BOM_PRINT_ACCENT }}
          >
            Executive Overview
          </h3>
          <p
            className="whitespace-pre-wrap"
            style={{
              fontSize: BOM_PRINT_BASE_FONT_SIZE_PX,
              lineHeight: BOM_PRINT_BASE_LINE_HEIGHT,
              fontWeight: 400,
              letterSpacing: 0,
              color: BOM_PRINT_TEXT,
            }}
          >
            {overview}
          </p>
        </section>
      ) : null}

      {highlights.length > 0 ? (
        <section className="space-y-1 pdf-keep-together" data-testid="dtools-pdf-option-highlights">
          <h3
            className="font-semibold uppercase"
            style={{ fontSize: 9, letterSpacing: "0.05em", color: BOM_PRINT_ACCENT }}
          >
            Key Highlights
          </h3>
          <ul
            className="list-disc space-y-0.5 pl-4"
            style={{
              fontSize: BOM_PRINT_BASE_FONT_SIZE_PX,
              lineHeight: BOM_PRINT_BASE_LINE_HEIGHT,
              letterSpacing: 0,
              color: BOM_PRINT_TEXT,
            }}
          >
            {highlights.map((item, index) => (
              <li key={`${item}-${index}`}>{item}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="space-y-1">
        <DtoolsPdfEquipmentScheduleTable lines={scheduleLines} showItemPricing={showItemPricing} />
      </section>

      <div
        className="pdf-keep-together flex items-center justify-between gap-4 border-t-2 pt-3"
        style={{ borderColor: BOM_PRINT_ACCENT }}
        data-testid="dtools-pdf-option-total"
      >
        <p
          className="font-semibold uppercase"
          style={{ fontSize: 9, letterSpacing: "0.05em", color: BOM_PRINT_ACCENT }}
        >
          {totalLabel}
        </p>
        <p className="font-bold tabular-nums" style={{ fontSize: 12, color: BOM_PRINT_TEXT, fontWeight: 700 }}>
          {formatDtoolsMoney(view.overview.systemTotal)}
        </p>
      </div>
    </div>
  );
}
